const isMenuOpen = () => {
	const classList = document.querySelector('.ddc-document')?.classList;
	return classList?.contains('active');
};

/**
 * Waits for an element to appear in the DOM using a MutationObserver.
 *
 * @param {string} selector - The CSS selector of the target element.
 * @returns {Promise<Element>} A promise that resolves with the found element.
 */
const waitForElement = (selector) => {
	return new Promise((resolve) => {
		// Check immediately first
		const element = document.querySelector(selector);
		if (element) {
			return resolve(element);
		}

		// Set up the observer
		const observer = new MutationObserver((mutations, obs) => {
			if (element) {
				obs.disconnect();
				resolve(element);
			}
		});

		// Start observing
		observer.observe(document.documentElement, {
			childList: true,
			subtree: true
		});
	});
};

/**
 *
 * @param {function} callback
 * @returns
 */
export const onLdClientReady = async callback => {
	if (!window.ldClient) {
		document.addEventListener('LDClientReady', callback);
	} else {
		await window.ldClient.waitUntilReady();
		callback();
	}
};

/**
 *
 * @param {boolean} eventResolved
 * @param {string} eventName
 * @returns
 */
export const waitForEvent = (eventResolved, eventName) => {
	return new Promise(resolve => {
		if (eventResolved) {
			resolve(eventResolved);
		} else {
			window.addEventListener(
				eventName,
				data => {
					resolve(data);
				},
				{ once: true }
			);
		}
	});
};

export const waitForMenu = async () => {
	const menuOpenEventName = 'mobile-navbar/open';
	// targetElements targets both modern and legacy menu elements, respectively
	const targetElements = ['.ddc-mobile-fragment-nav', '[data-location~="primary-menu"]'];

	return new Promise(resolve => {
		if (isMenuOpen()) {
            Promise.race(targetElements.map(selector => waitForElement(selector)))
                .then(() => resolve(true))
                .catch(() => resolve(false));
        } else {
			const handleMenuOpenEvent = async () => {
                Promise.race(targetElements.map(selector => waitForElement(selector)))
                    .then(() => {
                        resolve(true);
                        window.DDC?.pubsub?.unsubscribe(menuOpenEventName);
                    })
                    .catch(() => resolve(false));
            };

			window.DDC?.pubsub?.subscribe(menuOpenEventName, handleMenuOpenEvent);
		}
	});
};

export const isDocumentReady = () => {
	return new Promise(resolve => {
		if (document.readyState !== 'loading') {
			resolve();
		} else {
			document.addEventListener('DOMContentLoaded', resolve, { once: true });
		}
	});
};

/**
 *
 * @returns {Promise<any>}
 */
export const isWIAPILoaded = async () => {
	return waitForEvent(
		window.DDC?.PrivateAPI?.integrationsLoaded,
		'WIAPILoaded'
	);
};

export const waitForObject = async (objectPath, interval = 50, timeout = 5000) => {
	return new Promise((resolve, reject) => {
		let checkObject;
		const timeoutHandler = setTimeout(() => {
			clearInterval(checkObject);
			reject(new Error(`Timed out after ${timeout}ms waiting for ${objectPath} to become available`));
		}, timeout);

		checkObject = setInterval(() => {
			const parts = objectPath.split('.');
			let current = window;
			const objectExists = parts.every(part => {
				if (current[part] === undefined) {
					return false;
				}
				current = current[part];
				return true;
			});
			if (!objectExists) {
				return;
			}
			clearInterval(checkObject);
			clearTimeout(timeoutHandler);
			resolve(true);
		}, interval);
	});
};
